import { useEffect, useState } from "react";
import Icon from "../../../../components/base/Icon";
import Indicator from "../../../../components/base/Indicator";
import { useHub } from "../../../../core/Global";
import PaymentMethod from "../models/PaymentMethod";
import PaymentCardView from "./PaymentCardView";
import NewCardOption from "./NewCardOption";


// Sentinel selection meaning "pay with a brand-new card via Stripe Checkout".
export const NEW_CARD = "__new_card__"


interface IProps {
    selectedMethodId?: string
    onChange: (methodId: string) => void
    // Raised while the cards are being fetched, so a drawer around this can hold its own submit
    // button back. Without it a customer can press pay before there is a card to pay with.
    onLoadingChange?: (loading: boolean) => void
    // Only the saved cards. For a charge that has no Stripe Checkout behind it -- resuming a
    // paused account -- a new card is added on the payment methods page first.
    noNewCard?: boolean
}

// Saved cards + a "new card" option, with single-selection. Fetches the account's payment methods and
// pre-selects the primary (or the new-card option when there are none). Shared by the pay drawers.
export default function PaymentMethodPicker(props: IProps) {

    const hub = useHub()
    const [methods, methodsSet] = useState<PaymentMethod[]>([])
    const [fetching, fetchingSet] = useState<boolean>(true)

    useEffect(() => {
        props.onLoadingChange?.(true)

        hub.Get("/accounts/profile/paymentmethods/",
            res => {
                const data: PaymentMethod[] = res?.data ?? []
                methodsSet(data)
                const primary = data.find(m => m.isPrimary) ?? data[0]
                if (primary?.id) props.onChange(primary.id)
                else if (!props.noNewCard) props.onChange(NEW_CARD)
                fetchingSet(false)
                props.onLoadingChange?.(false)
            },
            () => {
                // A failure still has to end the wait. Nothing was loaded, so the only thing left
                // to offer is a new card.
                if (!props.noNewCard) props.onChange(NEW_CARD)
                fetchingSet(false)
                props.onLoadingChange?.(false)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (fetching) return <div className="text-center py-8"><Indicator /></div>

    return (
        <div className="d-flex flex-column gap-3">
            {
                methods.map(m => {
                    const selected = props.selectedMethodId === m.id
                    return (
                        <div key={m.id}
                            className={`d-flex align-items-center p-4 rounded border cursor-pointer ${selected ? "border-primary bg-light-primary" : "border-gray-300"}`}
                            onClick={() => props.onChange(m.id!)}>
                            <PaymentCardView item={m} />
                            <div className="ms-auto">
                                <Icon
                                    icon={selected ? "circle-check" : "circle"}
                                    type="solid"
                                    size={20}
                                    className={selected ? "text-primary" : "text-gray-400"} />
                            </div>
                        </div>
                    )
                })
            }

            {
                !props.noNewCard &&
                <NewCardOption
                    selected={props.selectedMethodId === NEW_CARD}
                    onClick={() => props.onChange(NEW_CARD)} />
            }
            {
                props.noNewCard && methods.length === 0 &&
                <div className="text-gray-600 fs-7">
                    There is no card on the account yet. Add one on the Payment Methods page first.
                </div>
            }
        </div>
    )
}
